<template>
  <div class="p-4" style="height: calc(100vh - 160px);">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">
        {{ t('tagsManager.tagsManager') }}
      </h1>
      <button class="btn" @click="openNewCollectionModal">
        <Icon icon="tabler:plus" class="mr-2" />
        {{ t('tagsManager.newCollection') }}
      </button>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 h-full grid-space-6">
      
      <div class="md:col-span-1 h-full card bg-base-100 shadow border dark:border-gray-700">
        <div class="card-body p-4 flex flex-col">
          <h2 class="card-title mb-4">{{ t('tagsManager.collections') }}</h2>
          <div v-if="isLoadingCollections === true" class="text-center py-4">
            <span class="loading loading-spinner"></span>
          </div>
          <ul v-else-if="collections.length > 0" class="menu p-0">
            <li v-for="collection in collections" :key="collection.collectionId">
              <a 
                @click.prevent="selectCollection(collection)" 
                
                :class="{'active': selectedCollection?.collectionId === collection.collectionId}"
                class="flex justify-between mb-4 shadow border border-gray-200 items-center group rounded-md" style="background-color: #f4f4f4;"
              >
                <span>{{ collection.collectionName }}</span>
                <div class="opacity-0 group-hover:opacity-100 transition-opacity space-x-1">
                    <button class="btn btn-sm btn-ghost" @click.stop="openEditCollectionModal(collection)">
                        <Icon icon="tabler:pencil" />
                    </button>
                     <button class="btn btn-sm btn-ghost text-error" @click.stop="confirmDeleteCollection(collection)">
                         <Icon icon="tabler:trash" />
                    </button>
                     <button class="btn btn-sm btn-ghost" @click.stop="openLinkDivisionModal(collection)">
                         <Icon icon="tabler:link" />
                    </button>
                </div>
              </a>
            </li>
          </ul>
           <p v-else class="text-gray-500 text-sm text-center py-4">
              {{ t('tagsManager.noCollectionsFound') }}
           </p>
        </div>
      </div>


      <div class="md:col-span-2 card bg-base-100 shadow border dark:border-gray-700 h-full">
        <div class="card-body p-4 flex flex-col">
           <div class="flex justify-between items-center mb-4">
             <h2 class="card-title">
                 {{ t('tagsManager.tags') }} {{ isDefinedAndNotEmpty(selectedCollection) != false ? ': ' + selectedCollection.collectionName : '' }}
             </h2>
             <div class="flex items-center space-x-2">
               <button 
                  class="btn btn-sm btn-secondary p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 hover:border-indigo-300 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" 
                  :disabled="selectedCollection === null || isImportingTags === true" 
                  @click="openNewTagModal">
                  <Icon icon="tabler:plus" class="mr-1" />
                  {{ t('tagsManager.newTag') }}
              </button>
              <button
                class="btn btn-sm btn-outline inline-flex items-center"
                :disabled="selectedCollection === null || isImportingTags === true"
                @click="openTagImportPicker"
              >
                <span v-if="isImportingTags === true" class="loading loading-spinner loading-xs mr-1"></span>
                <Icon v-else icon="tabler:file-upload" class="mr-1" />
                {{ isImportingTags === true ? t('tagsManager.importingTags') : t('tagsManager.importTags') }}
              </button>
              <input
                ref="tagImportInput"
                type="file"
                class="hidden"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                @change="handleTagImport"
              />
             </div>
           </div>
          
           <div v-if="selectedCollection === null" class="text-center text-gray-500 py-10">
               {{ t('tagsManager.selectCollectionToViewTags') }}
           </div>
           <div v-else-if="isLoadingTags === true" class="text-center py-10">
              <span class="loading loading-spinner"></span>
           </div>
           <div v-else-if="tags.length > 0" class="space-y-2">
               <div v-for="tag in tags" :key="tag.tagId" class="p-3 rounded-md bg-base-200 dark:bg-gray-700 flex justify-between items-center group">
                   <div class="flex items-center space-x-2">
                       <Icon icon="tabler:tag" width="26" height="26" class="text-gray-500 mr-2 dark:text-gray-400 flex-shrink-0" />
                       <div>
                           <p class="font-medium">{{ tag.tagName }}</p>
                           <p class="text-sm text-gray-500 dark:text-gray-400">{{ tag.tagDescription != undefined ? tag.tagDescription : t('tagsManager.noDescription','No description') }}</p>
                       </div>
                   </div>
                   <div class="opacity-0 group-hover:opacity-100 transition-opacity space-x-1">
                        <button class="btn btn-sm btn-ghost" @click.stop="openEditTagModal(tag)">
                            <Icon icon="tabler:pencil" />
                        </button>
                         <button class="btn btn-sm btn-ghost text-error" @click.stop="confirmDeleteTag(tag)">
                             <Icon icon="tabler:trash" />
                        </button>
                   </div>
               </div>
           </div>
           <div v-else class="text-center text-gray-500 py-10">
               {{ t('tagsManager.noTagsInCollection') }}
           </div>
        </div>
      </div>
    </div>


    <dialog ref="collectionDialog" class="modal modal-middle">
      <div class="modal-box">
        <h3 class="font-bold text-lg mb-4">{{ isEditingCollection === true ? t('tagsManager.edit') : t('tagsManager.new') }} {{ t('tagsManager.collection') }}</h3>
        <form @submit.prevent="saveCollection">
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">{{ t('tagsManager.collectionName') }}</span>
              <span class="label-text-alt text-error" v-if="!(collectionForm.name && collectionForm.name.length > 0)">Required</span>
            </label>
            <input type="text" v-model="collectionForm.name" class="input input-bordered" required :placeholder="t('tagsManager.collectionNamePlaceholder','Enter collection name')" />
          </div>

          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">{{ t('tagsManager.linkToDivisions') }}</span>
               <span class="label-text-alt text-error" v-if="collectionForm.selectedDivisionIds?.length === 0">Required</span>
            </label>
            <div v-if="isLoadingDivisions === true" class="text-center">
                <span class="loading loading-spinner loading-sm"></span>
            </div>
            <div v-else-if="availableDivisions.length === 0" class="text-sm text-gray-500">
                {{ t('tagsManager.noDivisionsFound') }}
            </div>
             <div v-else class="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                <label v-for="division in availableDivisions" :key="division.divisionId" class="label cursor-pointer py-1">
                  <span class="label-text">{{ division.divisionName }}</span> 
                  <input type="checkbox" 
                         :value="division.divisionId" 
                         v-model="collectionForm.selectedDivisionIds" 
                         class="checkbox checkbox-sm checkbox-primary" />
                </label>
            </div>
          </div>

          <div class="modal-action">
            <button type="button" class="btn" @click="closeCollectionModal">{{ t('tagsManager.cancel') }}</button>
            <button 
              type="submit" 
              class="btn"
              :disabled="(collectionForm.name.trim() === '') || (isEditingCollection !== false && (collectionForm.selectedDivisionIds?.length === 0))">
              {{ isEditingCollection !== false ? t('tagsManager.saveChanges') : t('tagsManager.create') }}
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button>close</button>
      </form>
    </dialog>

    <dialog ref="tagDialog" class="modal modal-middle">
      <div class="modal-box">
        <h3 class="font-bold text-lg mb-4">{{ isEditingTag === true ? t('tagsManager.edit') : t('tagsManager.new') }} {{ t('tagsManager.tag') }}</h3>
         <p class="text-sm mb-4">{{ t('tagsManager.forCollection') }}: {{ selectedCollection?.collectionName }}</p>
        <form @submit.prevent="saveTag">
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">{{ t('tagsManager.tagName') }}</span>
            </label>
            <input type="text" v-model="tagForm.name" class="input input-bordered" required :placeholder="t('tagsManager.tagNamePlaceholder','Enter tag name')" />
          </div>
           <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">{{ t('tagsManager.descriptionPrompt') }}</span>
            </label>
            <textarea v-model="tagForm.description" class="textarea textarea-bordered h-24" :placeholder="t('tagsManager.descriptionPrompt','Enter description or prompt')"></textarea>
          </div>
          <div class="modal-action">
            <button type="button" class="btn" @click="closeTagModal">{{ t('tagsManager.cancel') }}</button>
            <button type="submit" class="btn">{{ isEditingTag === true ? t('tagsManager.saveChanges') : t('tagsManager.create') }}</button>
          </div>
        </form>
      </div>
        <form method="dialog" class="modal-backdrop">
        <button>close</button>
      </form>
    </dialog>

     <dialog ref="divisionDialog" class="modal modal-middle">
        <div class="modal-box">
            <h3 class="font-bold text-lg mb-4">{{ t('tagsManager.linkDivisionsToCollection') }}: {{ selectedCollection?.collectionName }}</h3>
    
            <div v-if="isLoadingDivisions === true || isLoadingLinkedDivisions === true" class="text-center py-6">
                <span class="loading loading-spinner loading-lg"></span>
            </div>
             <div v-else-if="availableDivisions.length === 0" class="text-sm text-gray-500 py-6 text-center">
                {{ t('tagsManager.noDivisionsFoundToLink') }}
            </div>
            <div v-else class="border rounded-md p-2 max-h-60 overflow-y-auto space-y-1 mb-4">
                <label v-for="division in availableDivisions" :key="division.divisionId" class="label cursor-pointer py-1">
                  <span class="label-text">{{ division.divisionName }}</span> 
                  <input type="checkbox" 
                         :value="division.divisionId" 
                         v-model="divisionsToLink" 
                         class="checkbox checkbox-sm checkbox-primary" />
                </label>
            </div>

            <div class="modal-action">
                <button type="button" class="btn" @click="closeDivisionModal">{{ t('tagsManager.cancel') }}</button>
                <button type="button" class="btn" @click="saveDivisionLinks">{{ t('tagsManager.saveLinks') }}</button>
            </div>
        </div>
         <form method="dialog" class="modal-backdrop">
            <button @click="closeDivisionModal">close</button>
        </form>
    </dialog>

     <dialog ref="confirmDeleteDialog" class="modal modal-middle">
        <div class="modal-box">
            <h3 class="font-bold text-lg text-error flex items-center space-x-2">
                <Icon icon="tabler:alert-circle" class="flex-shrink-0" />
                {{ t('tagsManager.confirmDeletion') }}
            </h3>
            <p class="py-4">{{ t('tagsManager.confirmDeletionMessage', { itemName: itemToDelete?.name }) }} <br/> 
               <span v-if="itemToDelete?.type === 'collection'" class="text-sm text-warning">{{ t('tagsManager.confirmDeletionWarning') }}</span>
            </p>
            <div class="modal-action">
                <button class="btn" @click="closeConfirmDeleteModal">{{ t('tagsManager.cancel') }}</button>
                <button class="btn btn-error" @click="deleteItem">{{ t('tagsManager.delete') }}</button>
            </div>
        </div>
        <form method="dialog" class="modal-backdrop">
            <button>close</button>
        </form>
    </dialog>

  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import axios from '@/api/axiosInstance'; 
import { Icon } from '@iconify/vue/dist/offline';
import { useToast } from 'vue-toastification';
import { globalState } from '@/globalState'; 
import router from '@/router'; 
import { useI18n } from '@/i18n';
import * as XLSX from 'xlsx';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
const { t } = useI18n();

const toast = useToast();


const collections = ref([]);
const selectedCollection = ref(null);
const tags = ref([]);
const isLoadingCollections = ref(false);
const isLoadingTags = ref(false);
const isLoadingDivisions = ref(false);
const availableDivisions = ref([]);
const isLoadingLinkedDivisions = ref(false);


const collectionDialog = ref(null);
const tagDialog = ref(null);
const divisionDialog = ref(null);
const confirmDeleteDialog = ref(null);
const tagImportInput = ref(null);

const isEditingCollection = ref(false);
const isEditingTag = ref(false);
const isImportingTags = ref(false);

const collectionForm = ref({ id: null, name: '', selectedDivisionIds: [] });
const tagForm = ref({ id: null, name: '', description: '' });
const itemToDelete = ref(null);
const divisionsToLink = ref([]);


const getDivisionField = (raw, fields) => {
  if (raw === null || raw === undefined) {
    return undefined;
  }
  for (let i = 0; i < fields.length; i = i + 1) {
    const key = fields[i];
    if (raw[key] !== undefined && raw[key] !== null) {
      return raw[key];
    }
  }
  return undefined;
};

const normalizeDivisions = (rawList) => {
  const list = Array.isArray(rawList) === true ? rawList : [];
  const normalized = [];
  for (let i = 0; i < list.length; i = i + 1) {
    const raw = list[i];
    const idValue = getDivisionField(raw, ['divisionId', 'divisionid', 'id']);
    let nameValue = getDivisionField(raw, ['divisionName', 'divisionname', 'name']);
    if (idValue === undefined || idValue === null) {
      continue;
    }
    let normalizedId = idValue;
    if (typeof normalizedId === 'string') {
      const trimmed = normalizedId.trim();
      if (trimmed === '') {
        continue;
      }
      const parsed = parseInt(trimmed, 10);
      if (isNaN(parsed) === false) {
        normalizedId = parsed;
      } else {
        normalizedId = trimmed;
      }
    }
    if (nameValue === undefined || nameValue === null || String(nameValue).trim() === '') {
      nameValue = String(normalizedId);
    }
    normalized.push({
      divisionId: normalizedId,
      divisionName: String(nameValue)
    });
  }
  return normalized;
};

const normalizeCollections = (rawList) => {
  const list = Array.isArray(rawList) === true ? rawList : [];
  const normalized = [];
  for (let i = 0; i < list.length; i = i + 1) {
    const raw = list[i];
    const idValue = getDivisionField(raw, ['collectionId', 'collectionid', 'id']);
    let nameValue = getDivisionField(raw, ['collectionName', 'collectionname', 'name']);
    let divisionIds = getDivisionField(raw, ['divisionIds', 'divisionids', 'divisions']);
    if (idValue === undefined || idValue === null) {
      continue;
    }
    let normalizedId = idValue;
    if (typeof normalizedId === 'string') {
      const trimmed = normalizedId.trim();
      if (trimmed === '') {
        continue;
      }
      const parsed = parseInt(trimmed, 10);
      if (isNaN(parsed) === false) {
        normalizedId = parsed;
      } else {
        normalizedId = trimmed;
      }
    }
    if (nameValue === undefined || nameValue === null || String(nameValue).trim() === '') {
      nameValue = String(normalizedId);
    }
    if (Array.isArray(divisionIds) === false) {
      divisionIds = [];
    }
    normalized.push({
      collectionId: normalizedId,
      collectionName: String(nameValue),
      divisionIds: divisionIds
    });
  }
  return normalized;
};

const getAuthToken = () => {
  const token = document.cookie.split('; ').find(row => row.startsWith('authToken=') === true)?.split('=')[1];
  if (token === undefined || token === null) {
    return '';
  }
  return token;
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const handleApiError = (error, context = 'operation') => {
    console.error(`Error during ${context}:`, error);
    if (error?.response?.status === 401 || error?.response?.status === 403) {
        toast.error('Authentication error. Redirecting to login.');
        router.push('/login');
    } else if (error?.response?.data?.message) {
        toast.error(`Error: ${error.response.data.message}`);
    } else {
        toast.error(`An unexpected error occurred during ${context}.`);
    }
};


const fetchCollections = async () => {
    isLoadingCollections.value = true;
    const headers = getAuthHeaders();
    try {
        const response = await axios.get(`${globalState.apiEndpoint}/api/tags/collections`, { headers });
        collections.value = normalizeCollections(response.data);
     
        if (selectedCollection.value !== null) {
            const stillExists = collections.value.find(c => c.collectionId === selectedCollection.value.collectionId);
            if (stillExists === undefined) {
                selectCollection(null); 
            }
        }
        
        if (selectedCollection.value === null && collections.value.length > 0) {
           selectCollection(collections.value[0]);
        } else if (collections.value.length === 0) {
            selectCollection(null);
        }

    } catch (error) {
        handleApiError(error, 'fetching collections');
        collections.value = [];
        selectCollection(null);
    } finally {
        isLoadingCollections.value = false;
    }
};

const fetchDivisions = async () => {
    isLoadingDivisions.value = true;
    availableDivisions.value = [];
    const headers = getAuthHeaders();
    if (headers === null) return;

    try {
        const response = await axios.get(`${globalState.apiEndpoint}/api/divisions`, { headers });
        availableDivisions.value = normalizeDivisions(response.data);
    } catch (error) {
        handleApiError(error, 'fetching divisions');
        availableDivisions.value = [];
    } finally {
        isLoadingDivisions.value = false;
    }
};

const fetchLinkedDivisions = async (collectionId) => {
    isLoadingLinkedDivisions.value = true;
    const headers = getAuthHeaders();
    if (headers === null) return;
    try {
        const response = await axios.get(`${globalState.apiEndpoint}/api/tags/collections/${collectionId}/divisions`, { headers });
        const normalized = normalizeDivisions(response.data);
        divisionsToLink.value = normalized.map(d => d.divisionId);
    } catch (error) {
        handleApiError(error, `fetching linked divisions for collection ${collectionId}`);
        divisionsToLink.value = [];
    } finally {
        isLoadingLinkedDivisions.value = false;
    }
};

const fetchTagsForCollection = async (collectionId) => {
    isLoadingTags.value = true;
    tags.value = [];
    const headers = getAuthHeaders();
    if (headers === null) return;

    try {
        const response = await axios.get(`${globalState.apiEndpoint}/api/tags/collections/${collectionId}/tags`, { headers });
        tags.value = response.data;
    } catch (error) {
       handleApiError(error, `fetching tags for collection ${collectionId}`);
       tags.value = [];
    } finally {
        isLoadingTags.value = false;
    }
};


const selectCollection = (collection) => {
    selectedCollection.value = collection;
    if (collection !== null) {
        fetchTagsForCollection(collection.collectionId);
    } else {
        tags.value = [];
    }
};


watch(selectedCollection, (newCollection) => {
    if (newCollection !== null) {
        fetchTagsForCollection(newCollection.collectionId);
    } else {
        tags.value = [];
    }
}, { immediate: true }); 



const openNewCollectionModal = () => {
    isEditingCollection.value = false;
    collectionForm.value = { id: null, name: '', selectedDivisionIds: [] };
    collectionDialog.value?.showModal();
};

const openEditCollectionModal = (collection) => {
    isEditingCollection.value = true;
    collectionForm.value = { id: collection.collectionId, name: collection.collectionName, selectedDivisionIds: collection.divisionIds };
    collectionDialog.value?.showModal();
};

const closeCollectionModal = () => {
    collectionDialog.value?.close();
};

const saveCollection = async () => {
    const headers = getAuthHeaders();
    if (headers === null) return;
    const url = isEditingCollection.value === true
        ? `${globalState.apiEndpoint}/api/tags/collections/${collectionForm.value.id}`
        : `${globalState.apiEndpoint}/api/tags/collections`;
    const method = isEditingCollection.value === true ? 'put' : 'post';
    const payload = { 
        collectionName: collectionForm.value.name,
        ...(isEditingCollection.value === false && { divisionIds: collectionForm.value.selectedDivisionIds })
    };
    const successMessage = isEditingCollection.value === true ? t('tagsManager.collectionUpdated') : t('tagsManager.collectionCreated');
    const context = isEditingCollection.value != false ? 'updating collection' : 'creating collection';

    try {
        await axios({ method, url, headers, data: payload });
        toast.success(successMessage);
        closeCollectionModal();
        await fetchCollections(); 
        
        if (isEditingCollection.value === false) {
            const newlyCreated = collections.value.find(c => c.collectionName === collectionForm.value.name);
             if (newlyCreated !== undefined) {
                 selectCollection(newlyCreated);
             }
        } else {
            const updatedCollection = collections.value.find(c => c.collectionId === collectionForm.value.id);
             if (updatedCollection !== undefined) {
                selectCollection(updatedCollection); 
            }
        }

    } catch (error) {
        handleApiError(error, context);
    }
};


const confirmDeleteCollection = (collection) => {
    itemToDelete.value = { type: 'collection', id: collection.collectionId, name: collection.collectionName };
    confirmDeleteDialog.value?.showModal();
};


const openNewTagModal = () => {
    if (selectedCollection.value === null) {
        toast.warning('Please select a collection first.');
        return;
    }
    isEditingTag.value = false;
    tagForm.value = { id: null, name: '', description: '' };
    tagDialog.value?.showModal();
};

const openEditTagModal = (tag) => {
    isEditingTag.value = true;
    tagForm.value = { id: tag.tagId, name: tag.tagName, description: tag.tagDescription };
    tagDialog.value?.showModal();
};

const closeTagModal = () => {
    tagDialog.value?.close();
};

const saveTag = async () => {
     if (selectedCollection.value === null) {
        toast.error('Cannot save tag without a selected collection.');
        return;
    }
    const headers = getAuthHeaders();
    if (headers === null) return;

    const url = isEditingTag.value === true
        ? `${globalState.apiEndpoint}/api/tags/tags/${tagForm.value.id}`
        : `${globalState.apiEndpoint}/api/tags/collections/${selectedCollection.value.collectionId}/tags`;
    const method = isEditingTag.value === true ? 'put' : 'post';
    const payload = { 
        tagName: tagForm.value.name,
        tagDescription: tagForm.value.description
    };
    const successMessage = isEditingTag.value === true ? t('tagsManager.tagUpdated') : t('tagsManager.tagCreated');
    const context = isEditingTag.value != false ? 'updating tag' : 'creating tag';

    try {
        await axios({ method, url, headers, data: payload });
        toast.success(successMessage);
        closeTagModal();
        fetchTagsForCollection(selectedCollection.value.collectionId); 
    } catch (error) {
        handleApiError(error, context);
    }
};

const openTagImportPicker = () => {
    if (selectedCollection.value === null) {
        toast.warning(t('tagsManager.selectCollectionFirstToImport'));
        return;
    }
    if (isImportingTags.value === true) {
        return;
    }
    tagImportInput.value?.click();
};

const isTagImportHeaderRow = (row) => {
    if (Array.isArray(row) === false) {
        return false;
    }
    const firstCell = row[0] !== undefined && row[0] !== null ? String(row[0]).trim().toLowerCase() : '';
    const secondCell = row[1] !== undefined && row[1] !== null ? String(row[1]).trim().toLowerCase() : '';
    const nameHeaders = ['name', 'tag', 'tag name', 'tagname'];
    const promptHeaders = ['prompt', 'description', 'tag prompt', 'tag description', 'tagdescription'];
    return nameHeaders.includes(firstCell) === true && promptHeaders.includes(secondCell) === true;
};

const extractTagsFromWorksheetRows = (rows) => {
    const normalizedRows = Array.isArray(rows) === true ? rows : [];
    const importedTags = [];
    let startIndex = 0;
    if (normalizedRows.length > 0 && isTagImportHeaderRow(normalizedRows[0]) === true) {
        startIndex = 1;
    }
    for (let i = startIndex; i < normalizedRows.length; i = i + 1) {
        const row = normalizedRows[i];
        if (Array.isArray(row) === false) {
            continue;
        }
        const rawName = row[0];
        const rawPrompt = row[1];
        if (rawName === undefined || rawName === null || String(rawName).trim() === '') {
            continue;
        }
        importedTags.push({
            tagName: String(rawName).trim(),
            tagDescription: rawPrompt !== undefined && rawPrompt !== null ? String(rawPrompt).trim() : ''
        });
    }
    return importedTags;
};

const handleTagImport = async (event) => {
    const input = event.target;
    const file = input?.files?.[0];
    if (file === undefined || file === null) {
        return;
    }
    if (selectedCollection.value === null) {
        toast.warning(t('tagsManager.selectCollectionFirstToImport'));
        input.value = '';
        return;
    }
    isImportingTags.value = true;
    try {
        const fileBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(fileBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        if (firstSheetName === undefined || firstSheetName === null) {
            toast.warning(t('tagsManager.importTagsNoValidRows'));
            return;
        }
        const worksheet = workbook.Sheets[firstSheetName];
        const worksheetRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, blankrows: false });
        const importedTags = extractTagsFromWorksheetRows(worksheetRows);
        if (importedTags.length === 0) {
            toast.warning(t('tagsManager.importTagsNoValidRows'));
            return;
        }
        const headers = getAuthHeaders();
        const collectionId = selectedCollection.value.collectionId;
        let importedCount = 0;
        let duplicateCount = 0;
        let failedCount = 0;
        for (let i = 0; i < importedTags.length; i = i + 1) {
            const tag = importedTags[i];
            try {
                await axios.post(
                    `${globalState.apiEndpoint}/api/tags/collections/${collectionId}/tags`,
                    {
                        tagName: tag.tagName,
                        tagDescription: tag.tagDescription
                    },
                    { headers }
                );
                importedCount = importedCount + 1;
            } catch (error) {
                if (error?.response?.status === 409) {
                    duplicateCount = duplicateCount + 1;
                } else if (error?.response?.status === 401 || error?.response?.status === 403) {
                    handleApiError(error, 'importing tags');
                    return;
                } else {
                    failedCount = failedCount + 1;
                }
            }
        }
        if (importedCount > 0) {
            await fetchTagsForCollection(collectionId);
            toast.success(t('tagsManager.importTagsSuccess', { count: importedCount, duplicates: duplicateCount, failed: failedCount }));
        } else if (duplicateCount > 0 && failedCount === 0) {
            toast.warning(t('tagsManager.importTagsDuplicatesOnly', { count: duplicateCount }));
        } else {
            toast.warning(t('tagsManager.importTagsNoRowsCreated', { duplicates: duplicateCount, failed: failedCount }));
        }
    } catch (error) {
        handleApiError(error, 'importing tags from xlsx');
    } finally {
        isImportingTags.value = false;
        input.value = '';
    }
};


const confirmDeleteTag = (tag) => {
     itemToDelete.value = { type: 'tag', id: tag.tagId, name: tag.tagName };
     confirmDeleteDialog.value?.showModal();
};



const closeConfirmDeleteModal = () => {
    confirmDeleteDialog.value?.close();
    itemToDelete.value = null;
};


const deleteItem = async () => {
    if (itemToDelete.value === null) return;

    const headers = getAuthHeaders();
    if (headers === null) return;

    const { type, id } = itemToDelete.value;
    let url = '';
    let successMessage = '';
    let context = '';

    if (type === 'collection') {
        url = `${globalState.apiEndpoint}/api/tags/collections/${id}`;
        successMessage = t('tagsManager.collectionDeleted');
        context = 'deleting collection';
    } else if (type === 'tag') {
        url = `${globalState.apiEndpoint}/api/tags/tags/${id}`;
         successMessage = t('tagsManager.tagDeleted');
         context = 'deleting tag';
    } else {
        toast.error('Unknown item type for deletion.');
        closeConfirmDeleteModal();
        return;
    }

    try {
        await axios.delete(url, { headers });
        toast.success(successMessage);
        
        if (type === 'collection') {
            await fetchCollections(); 
             if (selectedCollection.value?.collectionId === id) {
                 selectCollection(null); 
             }
        } else if (type === 'tag' && selectedCollection.value !== null) {
            fetchTagsForCollection(selectedCollection.value.collectionId); 
        }
        
    } catch (error) {
       handleApiError(error, context);
    } finally {
        closeConfirmDeleteModal();
    }
};


const openLinkDivisionModal = async (collection) => {
     if (collection === null) return;
     selectCollection(collection); 
     await fetchLinkedDivisions(collection.collectionId);
     divisionDialog.value?.showModal();
     
};

const closeDivisionModal = () => {
    divisionDialog.value?.close();
    divisionsToLink.value = [];
};

const saveDivisionLinks = async () => {
    if (selectedCollection.value === null) {
        toast.error('No collection selected.');
        return;
    }
    const headers = getAuthHeaders();
    if (headers === null) return;

    const collectionId = selectedCollection.value.collectionId;
    const payload = { divisionIds: divisionsToLink.value };

    try {
        await axios.put(`${globalState.apiEndpoint}/api/tags/collections/${collectionId}/divisions`, payload, { headers });
        toast.success(t('tagsManager.divisionLinksUpdated'));
        closeDivisionModal();
    } catch (error) {
        handleApiError(error, 'updating division links');
    }
};


onMounted(() => {
  fetchCollections();
  fetchDivisions();
});
</script>

<style scoped>
.menu li a.active {
  background-color: hsl(var(--p)); 
  color: hsl(var(--pc)); 
}

.menu li a:hover:not(.active) {
   background-color: hsl(var(--b2)); 
}


</style>